import React, { useCallback, useState } from "react";
import { usePitchPanelExpanded } from "@/features/scouting/hooks/usePitchPanelExpanded";

import CollapsiblePitchPanelView, {
  type CollapsiblePitchPanelWidth,
} from "./CollapsiblePitchPanelView";

/**
 * Keeps the mounted content and its scroll position while changing panel width.
 * @param root0 - Panel configuration.
 * @param root0.side - Edge of the pitch.
 * @param root0.children - Mounted panel content.
 * @param root0.persistenceKey - Session identity shared across view switches.
 * @param root0.width - Expanded width preset; the frame's 344 px side panel
 * unless the scenario's 400 px Add-players panel is asked for (SE-8486).
 */
const CollapsiblePitchPanel = ({
  side,
  children,
  width = "side-panel",
  persistenceKey,
}: {
  side: "left" | "right";
  children: React.ReactNode;
  width?: CollapsiblePitchPanelWidth;
  persistenceKey?: string;
}) => {
  const [expanded, setExpanded] = useState(true);
  const toggle = useCallback(() => setExpanded(value => !value), []);
  const pointsLeft = side === "left" ? expanded : !expanded;
  const session = usePitchPanelExpanded(`${persistenceKey ?? "local"}:${side}`);
  const isExpanded = persistenceKey ? session.expanded : expanded;

  return (
    <CollapsiblePitchPanelView
      side={side}
      expanded={isExpanded}
      onToggle={persistenceKey ? session.toggle : toggle}
      pointsLeft={
        persistenceKey
          ? side === "left"
            ? isExpanded
            : !isExpanded
          : pointsLeft
      }
      width={width}
    >
      {children}
    </CollapsiblePitchPanelView>
  );
};

export { CollapsiblePitchPanel };
